/**
 * Toast Store (Enhanced)
 * State management for toast notifications
 *
 * Features:
 * - Bottom-positioned on mobile with safe-area
 * - Stacks up to 3 toasts (configurable)
 * - Auto-dismiss after configurable duration
 * - 4 variants: success/error/warning/info
 * - Group count tracking
 * - Update existing toasts
 * - Persist middleware support (optional)
 * - User preferences integration (P1-T8)
 *
 * @version 2.1.0 - Added toast preferences support
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

// Toast type keys that map to preferences
export type ToastTypeKey =
  | 'page_saved'
  | 'page_deleted'
  | 'page_created'
  | 'share_notification'
  | 'comment_notification'
  | 'general'; // For toasts without specific type

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Group count (for displaying "×3") */
  count?: number;
  /** Timestamp when toast was created */
  createdAt?: number;
  /** Whether toast is being dismissed (for animations) */
  dismissing?: boolean;
  /** Toast type for preference checking */
  toastType?: ToastTypeKey;
}

interface ToastStore {
  toasts: Toast[];
  /** Maximum number of toasts to show at once */
  maxToasts: number;
  /** Add a new toast */
  addToast: (toast: Omit<Toast, 'id'>) => string;
  /** Update an existing toast */
  updateToast: (id: string, updates: Partial<Toast>) => void;
  /** Remove a specific toast */
  removeToast: (id: string) => void;
  /** Clear all toasts */
  clearAll: () => void;
  /** Set max toasts limit */
  setMaxToasts: (max: number) => void;
}

export const useToastStore = create<ToastStore>((set, get) => ({
  toasts: [],
  maxToasts: 3,

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const newToast: Toast = {
      ...toast,
      id,
      createdAt: Date.now(),
      count: toast.count || 1,
      dismissing: false,
    };

    set((state) => {
      // Limit to maxToasts
      const toasts = [...state.toasts, newToast].slice(-state.maxToasts);
      return { toasts };
    });

    // Auto-dismiss
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        // Mark as dismissing for animation
        set((state) => ({
          toasts: state.toasts.map((t) =>
            t.id === id ? { ...t, dismissing: true } : t
          ),
        }));

        // Remove after animation (300ms)
        setTimeout(() => {
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }));
        }, 300);
      }, duration);
    }

    return id;
  },

  updateToast: (id, updates) => {
    set((state) => ({
      toasts: state.toasts.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      ),
    }));
  },

  removeToast: (id) => {
    // Mark as dismissing for animation
    set((state) => ({
      toasts: state.toasts.map((t) =>
        t.id === id ? { ...t, dismissing: true } : t
      ),
    }));

    // Remove after animation
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, 300);
  },

  clearAll: () => {
    // Mark all as dismissing
    set((state) => ({
      toasts: state.toasts.map((t) => ({ ...t, dismissing: true })),
    }));

    // Clear after animation
    setTimeout(() => {
      set({ toasts: [] });
    }, 300);
  },

  setMaxToasts: (max) => {
    set({ maxToasts: Math.max(1, Math.min(max, 5)) }); // Limit between 1-5
  },
}));

/**
 * Helper hook for easy toast creation with preference checking (P1-T8)
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Page saved!', undefined, 'page_saved');
 *   // Toast will only show if user has enabled 'page_saved' notifications
 *
 * Note: Import useToastPreferencesStore dynamically to avoid circular dependencies
 */
export function useToast() {
  const { addToast } = useToastStore();

  const showToast = (
    message: string,
    variant: ToastVariant,
    action?: Toast['action'],
    duration?: number,
    toastType?: ToastTypeKey
  ) => {
    // If toastType is provided and not 'general', check preferences
    if (toastType && toastType !== 'general') {
      // Dynamically import to avoid circular dependency
      import('@features/notes/store/toastPreferencesStore').then(
        ({ useToastPreferencesStore }) => {
          const { isToastEnabled } = useToastPreferencesStore.getState();

          // Only show toast if enabled in preferences
          if (isToastEnabled(toastType)) {
            addToast({ message, variant, action, duration, toastType });
          }
        }
      );
    } else {
      // Always show general toasts (no preference filtering)
      addToast({ message, variant, action, duration, toastType });
    }
  };

  return {
    success: (message: string, action?: Toast['action'], toastType?: ToastTypeKey) =>
      showToast(message, 'success', action, undefined, toastType),
    error: (message: string, action?: Toast['action'], toastType?: ToastTypeKey) =>
      showToast(message, 'error', action, 7000, toastType),
    warning: (message: string, action?: Toast['action'], toastType?: ToastTypeKey) =>
      showToast(message, 'warning', action, undefined, toastType),
    info: (message: string, action?: Toast['action'], toastType?: ToastTypeKey) =>
      showToast(message, 'info', action, undefined, toastType),
  };
}
